import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { KompisStoreSelector } from '@inter-ikea-kompis/react-components';
import StoreSelectorModalEnum from '@inter-ikea-kompis/component-store-selector/lib/enums/StoreSelectorModalEnum';
import StoreSelectorLayoutEnum from '@inter-ikea-kompis/component-store-selector/lib/enums/StoreSelectorLayoutEnum';
import { SelectStoreEvent } from 'components/StoreSelectorSheet/StoreSelectorSheet';
import Box from 'ui/Box';
import { SpaceProps } from 'styled-system';
import { IStore, IStoreAvailability } from '@inter-ikea-kompis/types/lib';
import { hideStoreSelectorSheet, showStoreSelectorSheet } from 'ducks/sheets';
import { reportSetStoreLocation } from 'analytics/reporters';
import { ReportCurrentPageEnum } from '../HomeDelivery/HomeDelivery';
import { isHamburgerMenuOpen } from 'ducks/layout';
import { SheetAlignmentEnum } from '@inter-ikea-kompis/component-sheet';
import useIsRtl from 'hooks/useIsRtl';
import { useAppDispatch } from 'ducks/hooks';
import { StoreIdCookieInformationService } from '@inter-ikea-kompis/services';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { useServiceSettings } from 'contexts/serviceSettings';

type Props = SpaceProps & {
  storeId: string | null;
  stores: IStore[];
  storeAvailabilities?: IStoreAvailability[];
  openExternalStoreSelectorSheet?: boolean;
  currentPage: ReportCurrentPageEnum;
  id?: string;
};

const StoreSelector = ({
  storeId,
  stores,
  storeAvailabilities,
  openExternalStoreSelectorSheet,
  currentPage,
  id,
  ...props
}: Props) => {
  const dispatch = useAppDispatch();
  const { dexfSettings, translations } = useServiceSettings();
  const isHamburgerMenuVisible = useSelector(isHamburgerMenuOpen);
  const isRtl = useIsRtl();

  const [showStoresSheet, setShowStoresSheet] = useState<StoreSelectorModalEnum | undefined>(
    undefined
  );

  const storeService = new StoreIdCookieInformationService({ settings: dexfSettings });

  const onSelectStore = (event: SelectStoreEvent) => {
    reportSetStoreLocation({ page: currentPage, storeId: event.detail.storeId });
    storeService.setStoreId(event.detail.storeId);
    setShowStoresSheet(undefined);
  };

  const onModalOpen = (event: CustomEvent) => {
    if (openExternalStoreSelectorSheet) {
      dispatch(showStoreSelectorSheet());
    } else {
      setShowStoresSheet(event.detail.visibleModal);
    }
  };

  const onModalClose = (event: CustomEvent) => {
    openExternalStoreSelectorSheet
      ? dispatch(hideStoreSelectorSheet())
      : setShowStoresSheet(event.detail.visibleModal);
  };

  if (dexfSettings.kompis.serviceSettings.storeAvailabilityDataSource === 'disabled') {
    return null;
  }
  if (stores.length < 0) {
    return null;
  }

  if (
    currentPage === ReportCurrentPageEnum.hamburgerMenu &&
    showStoresSheet &&
    !isHamburgerMenuVisible
  ) {
    setShowStoresSheet(undefined);
  }

  return (
    <Box id={id} {...props}>
      {translations && dexfSettings && (
        <KompisStoreSelector
          data-testid="store-selector"
          storeId={storeId as string}
          stores={stores}
          onChange={onSelectStore}
          translations={translations.kompis}
          settings={dexfSettings}
          onModalOpen={onModalOpen}
          onModalClose={onModalClose}
          visibleModal={showStoresSheet}
          theme={SkapaTheme}
          layout={StoreSelectorLayoutEnum.link}
          storeAvailabilities={storeAvailabilities}
          sheetAlignment={isRtl ? SheetAlignmentEnum.left : SheetAlignmentEnum.right}
        />
      )}
    </Box>
  );
};

export default StoreSelector;
